import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  FlightLog,
  FlightLogConfig,
  FlightLogAircraft,
  FlightLogAuditEntry,
  FlightLogInputDraft,
  FlightType,
  JourneyBookView,
  JourneyEntryInputDraft,
  MaintenanceRecord,
} from '@jupi/shared'
import { api } from '@jupi/ui'

// The flight log's server interactions (/api/flight-logs). Every change
// invalidates the whole 'flightLog' tree: one flight moves the totals of
// every later flight on that aircraft, and the aircraft's current total.

export interface FlightLogPage {
  flights: FlightLog[]
  page: number
  pages: number
  total: number
}

export interface FlightLogFilter {
  registration?: string
  mine?: boolean
  page?: number
}

export const flightLogKeys = {
  all: ['flightLog'] as const,
  list: (filter: FlightLogFilter) => ['flightLog', 'list', filter] as const,
  detail: (flightId: string) => ['flightLog', 'detail', flightId] as const,
  aircraft: ['flightLog', 'aircraft'] as const,
  flightTypes: ['flightLog', 'flightTypes'] as const,
  crew: ['flightLog', 'crew'] as const,
  config: ['flightLog', 'config'] as const,
  book: (registration: string, bookNumber: number) => ['flightLog', 'book', registration, bookNumber] as const,
}

export function useFlightLogs(filter: FlightLogFilter) {
  const params = new URLSearchParams()
  if (filter.registration) params.set('registration', filter.registration)
  if (filter.mine) params.set('mine', 'true')
  if (filter.page && filter.page > 1) params.set('page', String(filter.page))
  return useQuery({
    queryKey: flightLogKeys.list(filter),
    queryFn: () => api.get<FlightLogPage>(`/api/flight-logs?${params}`),
    placeholderData: (previous) => previous,
  })
}

export function useFlightLog(flightId: string | undefined) {
  return useQuery({
    queryKey: flightLogKeys.detail(flightId ?? ''),
    queryFn: () => api.get<{ flight: FlightLog; audit: FlightLogAuditEntry[] }>(`/api/flight-logs/${flightId}`),
    enabled: Boolean(flightId),
  })
}

export function useFlightLogAircraft() {
  return useQuery({
    queryKey: flightLogKeys.aircraft,
    queryFn: () => api.get<{ aircraft: FlightLogAircraft[] }>('/api/flight-logs/aircraft').then((r) => r.aircraft),
  })
}

export function useFlightTypes() {
  return useQuery({
    queryKey: flightLogKeys.flightTypes,
    queryFn: () => api.get<{ flightTypes: FlightType[] }>('/api/flight-logs/flight-types').then((r) => r.flightTypes),
    staleTime: 10 * 60_000,
  })
}

export function useCrewMembers(enabled: boolean) {
  return useQuery({
    queryKey: flightLogKeys.crew,
    queryFn: () =>
      api.get<{ members: { userId: string; displayName: string }[] }>('/api/flight-logs/crew').then((r) => r.members),
    staleTime: 10 * 60_000,
    enabled,
  })
}

export function useFlightLogMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: flightLogKeys.all })
  return {
    create: useMutation({
      mutationFn: (body: FlightLogInputDraft) => api.post<{ flight: FlightLog }>('/api/flight-logs', body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ flightId, body }: { flightId: string; body: FlightLogInputDraft }) =>
        api.put<{ flight: FlightLog }>(`/api/flight-logs/${flightId}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (flightId: string) => api.delete<void>(`/api/flight-logs/${flightId}`),
      onSuccess: invalidate,
    }),
  }
}

export function useFlightLogConfig() {
  return useQuery({
    queryKey: flightLogKeys.config,
    queryFn: () => api.get<FlightLogConfig>('/api/flight-logs/config'),
    staleTime: 10 * 60_000,
  })
}

// One journey log book as the paper book's pages.
export function useJourneyBook(registration: string | undefined, bookNumber: number | undefined) {
  return useQuery({
    queryKey: flightLogKeys.book(registration ?? '', bookNumber ?? 0),
    queryFn: () => api.get<JourneyBookView>(`/api/flight-logs/book/${registration}/${bookNumber}`),
    enabled: Boolean(registration) && bookNumber != null,
  })
}

// The engineer's entries and blank rows in a book. Each answers with the
// whole book view: every row after the change has moved, as does the cache.
export function useJourneyEntryMutations(registration: string, bookNumber: number) {
  const queryClient = useQueryClient()
  const base = `/api/flight-logs/book/${registration}/${bookNumber}/entries`
  const invalidate = () => queryClient.invalidateQueries({ queryKey: flightLogKeys.all })
  return {
    create: useMutation({
      mutationFn: (body: JourneyEntryInputDraft) => api.post<JourneyBookView>(base, body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ entryId, body }: { entryId: number; body: JourneyEntryInputDraft }) =>
        api.put<JourneyBookView>(`${base}/${entryId}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (entryId: number) => api.delete<void>(`${base}/${entryId}`),
      onSuccess: invalidate,
    }),
  }
}

// The aircraft's maintenance records, for linking an entry to one.
export function useBookMaintenanceRecords(registration: string, bookNumber: number, enabled: boolean) {
  return useQuery({
    queryKey: ['flightLog', 'bookRecords', registration, bookNumber] as const,
    queryFn: () =>
      api
        .get<{ records: MaintenanceRecord[] }>(`/api/flight-logs/book/${registration}/${bookNumber}/maintenance-records`)
        .then((r) => r.records),
    enabled,
  })
}
